import { useState } from 'react';
import { Button, Field, Input, Modal, Textarea } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { toast } from '../../lib/toast';
import { useCreateShop, useDeleteShop, useShops, useUpdateShop, type Shop, type ShopInput } from './api';

/** Obchody, kde se nakupuje materiál — kontakt, zákaznické číslo, sleva, dodací doba. */

function ShopForm({ shop, onClose }: { shop: Shop | null; onClose: () => void }) {
  const [v, setV] = useState<ShopInput>({
    name: shop?.name ?? '',
    web: shop?.web ?? '',
    email: shop?.email ?? '',
    phone: shop?.phone ?? '',
    address: shop?.address ?? '',
    customerNo: shop?.customerNo ?? '',
    discount: shop?.discount ?? '',
    leadDays: shop?.leadDays ?? null,
    minOrder: shop?.minOrder ?? '',
    note: shop?.note ?? '',
  });
  const create = useCreateShop();
  const update = useUpdateShop();
  const pending = create.isPending || update.isPending;
  const set = (k: keyof ShopInput) => (e: { target: { value: string } }) => setV((s) => ({ ...s, [k]: e.target.value }));
  const save = () => {
    const payload = { ...v, leadDays: v.leadDays === null || (v.leadDays as unknown) === '' ? undefined : Number(v.leadDays) };
    const opts = { onSuccess: onClose, onError: (e: unknown) => toast.error((e as ApiError).message) };
    if (shop) update.mutate({ id: shop.id, ...payload }, opts);
    else create.mutate(payload, opts);
  };
  return (
    <Modal title={shop ? `Obchod — ${shop.name}` : 'Nový obchod'} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Název *" htmlFor="sh-name"><Input id="sh-name" autoFocus value={v.name} onChange={set('name')} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Web / e-shop" htmlFor="sh-web"><Input id="sh-web" placeholder="https://" value={v.web ?? ''} onChange={set('web')} /></Field>
          <Field label="Zákaznické číslo" htmlFor="sh-cust" hint="Bez hesel — jen číslo účtu / karty"><Input id="sh-cust" value={v.customerNo ?? ''} onChange={set('customerNo')} /></Field>
          <Field label="Telefon" htmlFor="sh-phone"><Input id="sh-phone" value={v.phone ?? ''} onChange={set('phone')} /></Field>
          <Field label="E-mail" htmlFor="sh-email"><Input id="sh-email" value={v.email ?? ''} onChange={set('email')} /></Field>
          <Field label="Sleva / podmínky" htmlFor="sh-disc"><Input id="sh-disc" placeholder="např. 8 % na kartu" value={v.discount ?? ''} onChange={set('discount')} /></Field>
          <Field label="Dodací doba (dní)" htmlFor="sh-lead"><Input id="sh-lead" inputMode="numeric" value={v.leadDays ?? ''} onChange={set('leadDays')} /></Field>
        </div>
        <Field label="Adresa / pobočka" htmlFor="sh-addr"><Input id="sh-addr" value={v.address ?? ''} onChange={set('address')} /></Field>
        <Field label="Min. odběr / doprava" htmlFor="sh-min"><Input id="sh-min" placeholder="doprava zdarma od 3 000 Kč" value={v.minOrder ?? ''} onChange={set('minOrder')} /></Field>
        <Field label="Poznámka" htmlFor="sh-note"><Textarea id="sh-note" rows={2} value={v.note ?? ''} onChange={set('note')} /></Field>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Zrušit</Button>
          <Button disabled={!v.name.trim() || pending} onClick={save}>{pending ? 'Ukládám…' : 'Uložit'}</Button>
        </div>
      </div>
    </Modal>
  );
}

export default function ShopsPage() {
  const { data: shops, isLoading } = useShops(true);
  const update = useUpdateShop();
  const del = useDeleteShop();
  const [editing, setEditing] = useState<Shop | null>(null);
  const [creating, setCreating] = useState(false);
  const onError = (e: unknown) => toast.error((e as ApiError).message);
  return (
    <div className="max-w-4xl">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Obchody</h1>
          <p className="text-sm text-zinc-500">Kde nakupujeme materiál. U materiálu pak „Kde koupit“ — odkaz, balení, poslední cena.</p>
        </div>
        <Button onClick={() => setCreating(true)}>+ Obchod</Button>
      </div>
      {isLoading && <p className="text-sm text-zinc-500">Načítám…</p>}
      {shops && shops.length === 0 && (
        <div className="rounded-lg border border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500 dark:border-zinc-700">Zatím žádný obchod.</div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {(shops ?? []).map((s) => (
          <div key={s.id} className={`rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900 ${s.active ? '' : 'opacity-50'}`}>
            <div className="flex items-start justify-between gap-2">
              <span className="font-semibold">{s.name}{!s.active && <span className="ml-1 text-xs font-normal text-zinc-400">(neaktivní)</span>}</span>
              {s.web && <a href={s.web} target="_blank" rel="noreferrer" className="shrink-0 text-xs text-sky-700 underline dark:text-sky-400">web</a>}
            </div>
            <div className="mt-1 space-y-0.5 text-xs text-zinc-500">
              {s.customerNo && <div>zák. č. {s.customerNo}</div>}
              {s.discount && <div>{s.discount}</div>}
              {(s.phone || s.email) && <div>{[s.phone, s.email].filter(Boolean).join(' · ')}</div>}
              {s.leadDays != null && <div>dodání {s.leadDays} d</div>}
              <div>{s._count?.sources ?? 0} materiálů · {s._count?.purchases ?? 0} ke koupi</div>
            </div>
            <div className="mt-2 flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => setEditing(s)}>Upravit</Button>
              <Button size="sm" variant="ghost" onClick={() => update.mutate({ id: s.id, active: !s.active }, { onError })}>{s.active ? 'Deaktivovat' : 'Aktivovat'}</Button>
              {(s._count?.sources ?? 0) === 0 && (s._count?.purchases ?? 0) === 0 && (
                <Button size="sm" variant="ghost" className="text-red-600 dark:text-red-400" onClick={() => { if (window.confirm(`Smazat obchod ${s.name}?`)) del.mutate(s.id, { onError }); }}>Smazat</Button>
              )}
            </div>
          </div>
        ))}
      </div>
      {creating && <ShopForm shop={null} onClose={() => setCreating(false)} />}
      {editing && <ShopForm shop={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
